import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import App from 'common/backbone-app';
import Wahanda from 'common/wahanda';
import { BookingsListAnalytics } from 'common/analytics';
import { Button } from 'components/common/Button';
import { Download } from 'components/common/Icon';
import { GoTo, Direction } from 'components/common/GoTo';
import { navigateBookingsReportHash } from 'src/url-states/reports-bookings';
import {
  CUSTOM_DATE_FILTER_REGEX,
  FILTER_TYPE_FUTURE,
  FILTER_TYPE_ALL,
  getEntityFilter,
  getFilterDates,
  getTypeFilter,
  mergeBookingsEnquiriesData,
} from 'services/bookings-report';
import type { BookingsEnquiriesModel, BookingsReportTableRow } from 'services/bookings-report';
import { downloadBookingsCsv } from 'services/bookings-csv';
import {
  MenuState,
  useBookingsCustomRangeValidation,
  useBookingsDatepicker,
  useBookingsDrawerCloseHandler,
  useBookingsFetchOnFilters,
  useBookingsHomeTableFilteredDispatcher,
  useBookingsMountGuard,
  useBookingsOutsideClick,
  useBookingsPageView,
  useBookingsRefreshEvents,
  useHydrateBookingsFilters,
} from './hooks';
import { BookingsReportTable } from './BookingsReportTable';
import style from './style.scss';

const STORAGE_FILTER_TYPE_KEY = 'home-filter-type';

const ALL_LEAVES = new Set([FILTER_TYPE_ALL, 'BCX', 'BRJ', FILTER_TYPE_FUTURE]);

const rootTypeFromLeaf = (filterType: string): 'ALL' | 'AAP' =>
  ALL_LEAVES.has(filterType) ? 'ALL' : 'AAP';

const buildSubtypeOptions = (root: 'ALL' | 'AAP') => {
  const L = Wahanda.lang.home.table.filters.type;
  if (root === 'ALL') {
    return [
      { value: FILTER_TYPE_ALL, label: L.all },
      { value: 'BCX', label: L.cancelled },
      { value: 'BRJ', label: L.rejected },
      { value: FILTER_TYPE_FUTURE, label: L.future },
    ];
  }
  return [
    { value: 'AAP', label: L.all },
    { value: 'AWH', label: L.soldTreatwell },
    { value: 'AWG', label: L.soldWidget },
    { value: 'ADR', label: L.createdInConnect },
    { value: 'ACP', label: L.completed },
  ];
};

const DATE_PRESETS = () => {
  const L = Wahanda.lang.home.table.filters.date;
  return [
    { value: '1M', label: L.months1 },
    { value: '2M', label: L.months2 },
    { value: '3M', label: L.months3 },
    { value: '6M', label: L.months6 },
    { value: '12M', label: L.months12 },
    { value: 'CUST', label: L.custom },
  ];
};

const BookingsReport = () => {
  const [filterType, setFilterType] = useState(FILTER_TYPE_ALL);
  const [filterDate, setFilterDate] = useState('1M');
  const [rows, setRows] = useState<BookingsReportTableRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [pickingCustom, setPickingCustom] = useState(false);
  const [openMenu, setOpenMenu] = useState<MenuState>(null);
  const [hydratedFromHash, setHydratedFromHash] = useState(false);
  const customFilterSetRef = useRef(false);
  const homeDataRef = useRef<HTMLDivElement>(null);
  const customRangeFormRef = useRef<HTMLFormElement>(null);
  const dateFromRef = useRef<HTMLInputElement>(null);
  const dateToRef = useRef<HTMLInputElement>(null);
  const fetchTsRef = useRef(0);
  const isMountedRef = useBookingsMountGuard(fetchTsRef);
  const emitHomeTableFiltered = useBookingsHomeTableFilteredDispatcher();

  const rootType = rootTypeFromLeaf(filterType);
  const typeMenuLabel = useMemo(() => {
    const L = Wahanda.lang.home.table.filters.type;
    return rootType === 'ALL' ? L.everything : L.appointments;
  }, [rootType]);

  const subtypeOptions = useMemo(() => buildSubtypeOptions(rootType), [rootType]);
  const subtypeLabel =
    subtypeOptions.find((o) => o.value === filterType)?.label ?? subtypeOptions[0].label;
  const datePresets = useMemo(() => DATE_PRESETS(), []);

  const filterDateIsCustom = CUSTOM_DATE_FILTER_REGEX.test(filterDate);
  const dateLabel =
    pickingCustom || filterDateIsCustom
      ? Wahanda.lang.home.table.filters.date.custom
      : datePresets.find((o) => o.value === filterDate)?.label ?? datePresets[0].label;

  const showDateFilters = filterType !== FILTER_TYPE_FUTURE;
  const showCustomRange = showDateFilters && (pickingCustom || filterDateIsCustom);

  useLayoutEffect(() => {
    if (filterDateIsCustom) {
      customFilterSetRef.current = true;
    }
  }, [filterDateIsCustom]);

  const fetchRows = useCallback(() => {
    if (!Wahanda.Permissions.viewBookings()) {
      return;
    }
    if (!isMountedRef.current) {
      return;
    }
    const type = filterType;
    const date = filterDate;
    const ts = Date.now();
    fetchTsRef.current = ts;
    setIsLoading(true);
    setIsError(false);
    const dates = getFilterDates({ type, date });
    const urlFilters: Record<string, string | null> = {
      entity: getEntityFilter(type),
      type: getTypeFilter(type),
      'date-from': dates.from,
      'date-to': dates.to,
    };
    if (type === FILTER_TYPE_FUTURE) {
      delete urlFilters['date-to'];
    }

    const model = new App.Models.BookingsEnquiries() as BookingsEnquiriesModel & {
      setUrlFilters: (f: Record<string, string | null>) => void;
      checkTS?: number;
      fetch: (o: { success: () => void; error: () => void }) => void;
    };
    model.setUrlFilters(urlFilters);
    model.checkTS = ts;
    model.fetch({
      success: () => {
        if (!isMountedRef.current || model.checkTS !== fetchTsRef.current) {
          return;
        }
        const merged = mergeBookingsEnquiriesData(model, type);
        setRows(merged);
        setIsError(false);
        setIsLoading(false);
      },
      error: () => {
        if (!isMountedRef.current || model.checkTS !== fetchTsRef.current) {
          return;
        }
        setIsError(true);
        setIsLoading(false);
      },
    });
  }, [filterDate, filterType, isMountedRef]);

  useHydrateBookingsFilters({
    storageKey: STORAGE_FILTER_TYPE_KEY,
    setFilterType,
    setFilterDate,
    setHydratedFromHash,
  });
  useBookingsPageView(hydratedFromHash);
  useBookingsFetchOnFilters({
    hydratedFromHash,
    filterType,
    filterDate,
    fetchRows,
  });
  useBookingsRefreshEvents(fetchRows);
  useBookingsDrawerCloseHandler();
  useBookingsOutsideClick(setOpenMenu);
  useBookingsDatepicker({
    showCustomRange,
    dateFromRef,
    dateToRef,
    filterType,
    filterDate,
    customFilterSetRef,
  });

  const applyFilterChange = (nextType: string, nextDate: string) => {
    setFilterType(nextType);
    setFilterDate(nextDate);
    if (!CUSTOM_DATE_FILTER_REGEX.test(nextDate)) {
      setPickingCustom(false);
    }
    Wahanda.LocalStorage.set(STORAGE_FILTER_TYPE_KEY, nextType);
    navigateBookingsReportHash(nextType, nextDate);
    setOpenMenu(null);
    emitHomeTableFiltered(nextType, nextDate);
  };

  const onRootTypePick = (value: 'ALL' | 'AAP') => {
    setPickingCustom(false);
    const defaultLeaf = value === 'ALL' ? FILTER_TYPE_ALL : 'AAP';
    applyFilterChange(defaultLeaf, filterDate);
  };

  const onSubtypePick = (value: string) => {
    setPickingCustom(false);
    applyFilterChange(value, filterDate);
  };

  const onDatePresetPick = (value: string) => {
    if (value === 'CUST') {
      setPickingCustom(true);
      setOpenMenu(null);
      return;
    }
    setPickingCustom(false);
    customFilterSetRef.current = false;
    applyFilterChange(filterType, value);
  };

  const applyCustomDateRange = () => {
    const formEl = customRangeFormRef.current;
    const $form = formEl ? $(formEl) : $();
    if (!$form.length || !$form.validate().valid()) {
      return;
    }
    const fromEl = dateFromRef.current;
    const toEl = dateToRef.current;
    if (!fromEl || !toEl) {
      return;
    }
    const strFrom = Wahanda.Date.toApiString($(fromEl).datepicker('getDate') as Date);
    const strTo = Wahanda.Date.toApiString($(toEl).datepicker('getDate') as Date);
    if (strFrom > strTo) {
      $form.validate().showErrors({
        'filter-date-to': Wahanda.lang.shared.errors.dateMustBeGreater,
      });
      return;
    }
    customFilterSetRef.current = true;
    setPickingCustom(false);
    applyFilterChange(filterType, `C_${strFrom}_${strTo}`);
  };

  const onRowClick = useCallback((row: BookingsReportTableRow) => {
    BookingsListAnalytics.trackOpenOrderDialog();
    if (row.type === 'order') {
      const view = new App.Views.Dialog.Order2({
        model: new App.Models.Order({ id: row.id }),
        updateUrl: false,
      });
      view.render();
      view.open();
    } else if (row.type === 'appointment') {
      if (Wahanda.Features.isEnabled('THY-new-appointment-drawer-desktop')) {
        App.ES6.Initializers.AppointmentDetails({
          appointment: {
            date: row.appointmentDate,
            venueCustomerId: row.venueCustomerId,
            orderId: row.orderId,
          },
          context: 'bookings',
        }).render();
      } else {
        App.Views.Forms.Appointment2.openById(row.id as number);
      }
    }
  }, []);

  useBookingsCustomRangeValidation({
    showCustomRange,
    customRangeFormRef,
  });

  if (!Wahanda.Permissions.viewBookings()) {
    return null;
  }

  return (
    <div id="bookings" className="section-main">
      <div className="reports-bookings">
        <div className="home-filters">
          <div
            id="filter-type"
            className={`filter-wrapper${openMenu === 'type' ? ' open' : ''}`}
            onClick={(ev) => {
              ev.stopPropagation();
              setOpenMenu((m) => (m === 'type' ? null : 'type'));
            }}
            role="presentation"
          >
            <div className="filter-current">
              {typeMenuLabel}
              <div className="handler">
                <div className="icons-arrow-bottom2" />
              </div>
            </div>
            <ul
              className="filter-ddown"
              style={{ display: openMenu === 'type' ? 'block' : 'none' }}
            >
              <li
                className={rootType === 'ALL' ? 'on' : ''}
                onClick={(ev) => {
                  ev.stopPropagation();
                  onRootTypePick('ALL');
                }}
                role="presentation"
              >
                {Wahanda.lang.home.table.filters.type.everything}
              </li>
              <li
                className={`subitem${rootType === 'AAP' ? ' on' : ''}`}
                onClick={(ev) => {
                  ev.stopPropagation();
                  onRootTypePick('AAP');
                }}
                role="presentation"
              >
                {Wahanda.lang.home.table.filters.type.appointments}
              </li>
            </ul>
          </div>

          <div
            id="filter-subtype"
            className={`filter-wrapper${openMenu === 'subtype' ? ' open' : ''}`}
            onClick={(ev) => {
              ev.stopPropagation();
              setOpenMenu((m) => (m === 'subtype' ? null : 'subtype'));
            }}
            role="presentation"
          >
            <div className="filter-current">
              {subtypeLabel}
              <span className="count">{rows.length}</span>
              <div className="handler">
                <div className="icons-arrow-bottom2" />
              </div>
            </div>
            <ul
              className="filter-ddown"
              style={{ display: openMenu === 'subtype' ? 'block' : 'none' }}
            >
              {subtypeOptions.map((opt) => (
                <li
                  key={opt.value}
                  className={filterType === opt.value ? 'on' : ''}
                  data-value={opt.value}
                  onClick={(ev) => {
                    ev.stopPropagation();
                    onSubtypePick(opt.value);
                  }}
                  role="presentation"
                >
                  {opt.label}
                </li>
              ))}
            </ul>
          </div>

          {showDateFilters ? (
            <div
              id="filter-date"
              className={`filter-wrapper short${openMenu === 'date' ? ' open' : ''}`}
              onClick={(ev) => {
                ev.stopPropagation();
                setOpenMenu((m) => (m === 'date' ? null : 'date'));
              }}
              role="presentation"
            >
              <div className="filter-current act">
                {dateLabel}
                <div className="handler">
                  <div className="icons-arrow-bottom2" />
                </div>
              </div>
              <ul
                className="filter-ddown"
                style={{ display: openMenu === 'date' ? 'block' : 'none' }}
              >
                {datePresets.map((opt) => (
                  <li
                    key={opt.value}
                    className={
                      (opt.value === 'CUST' && (pickingCustom || filterDateIsCustom)) ||
                      (opt.value !== 'CUST' && filterDate === opt.value)
                        ? 'on'
                        : ''
                    }
                    data-value={opt.value}
                    onClick={(ev) => {
                      ev.stopPropagation();
                      onDatePresetPick(opt.value);
                    }}
                    role="presentation"
                  >
                    {opt.label}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div
            className={`filter-wrapper date-range${showCustomRange ? '' : ' hidden'}`}
            style={{ display: showCustomRange ? '' : 'none' }}
          >
            <form ref={customRangeFormRef}>
              <div className="txt-input txt-input-mini">
                <input
                  ref={dateFromRef}
                  type="text"
                  defaultValue=""
                  placeholder={Wahanda.lang.shared.from}
                  className="date-format-default"
                  id="filter-date-from"
                  name="filter-date-from"
                  onKeyDown={(ev) => {
                    if (ev.keyCode === 13) {
                      applyCustomDateRange();
                    }
                  }}
                />
              </div>
              <span className="sep">-</span>
              <div className="txt-input txt-input-mini">
                <input
                  ref={dateToRef}
                  type="text"
                  defaultValue=""
                  placeholder={Wahanda.lang.shared.until}
                  className="date-format-default"
                  id="filter-date-to"
                  name="filter-date-to"
                  onKeyDown={(ev) => {
                    if (ev.keyCode === 13) {
                      applyCustomDateRange();
                    }
                  }}
                />
              </div>
              <button
                type="button"
                className="button button-basic refresh a-custom-filter"
                onClick={(ev) => {
                  ev.stopPropagation();
                  applyCustomDateRange();
                }}
              >
                <div className="button-inner">
                  <div className="button-icon icons-refresh" />
                  &nbsp;
                </div>
              </button>
            </form>
          </div>
        </div>
        <div className={style.downloadContainer}>
          <Button
            colour="help"
            variant="hollow"
            icon={<Download />}
            label={Wahanda.lang.reports.sales.download}
            onClick={() => downloadBookingsCsv(rows)}
            disabled={isLoading || isError}
          />
        </div>
        <GoTo className={style.linkBack} href="/" direction={Direction.backward}>
          {Wahanda.lang.reports.tabs.reports}
        </GoTo>
        <BookingsReportTable
          homeDataRef={homeDataRef}
          isError={isError}
          isLoading={isLoading}
          onRowClick={onRowClick}
          rows={rows}
        />
      </div>
    </div>
  );
};

export default BookingsReport;
